import React, { useState, useRef } from 'react';
import { Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getImageUrl, getOptimizedImageUrl } from '@/lib/bunnyStorage';

const DoubleTapImage = ({ image, isLiked: initiallyLiked, onLike, onClick, width, ...props }) => {
  const [isLiked, setIsLiked] = useState(initiallyLiked);
  const [showHeart, setShowHeart] = useState(false);
  const clickTimeoutRef = useRef(null);
  const clickCountRef = useRef(0);

  const handleClick = (e) => {
    clickCountRef.current += 1;

    if (clickCountRef.current === 1) {
      // First click - wait to see if there's a second click
      clickTimeoutRef.current = setTimeout(() => {
        if (clickCountRef.current === 1) {
          // Single click - trigger onClick if provided
          if (onClick) {
            onClick(e);
          }
        }
        clickCountRef.current = 0;
      }, 300); // Wait 300ms for potential double click
    } else if (clickCountRef.current === 2) {
      // Double click detected
      clearTimeout(clickTimeoutRef.current);
      clickCountRef.current = 0;

      // Handle double tap like
      const newLikedState = !isLiked;
      setIsLiked(newLikedState);
      if (newLikedState) {
        setShowHeart(true);
        setTimeout(() => setShowHeart(false), 1000);
      }
      if (onLike) {
        onLike(image.id, newLikedState);
      }
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      onClick={handleClick}
      className="relative cursor-pointer rounded-xl overflow-hidden bg-gray-100 hover:opacity-95 transition-all duration-300 shadow-sm hover:shadow-md"
      {...props}
    >
      <img
        src={width ? getOptimizedImageUrl(image.cloudinary_public_id, { width }) : getImageUrl(image.cloudinary_public_id)}
        alt={image.alt_text || 'Gallery image'}
        className="w-full object-cover select-none"
        style={{ width: '100%', height: 'auto', display: 'block' }}
        loading="lazy"
      />

      {/* Like indicator */}
      {isLiked && (
        <div className="absolute top-3 right-3">
          <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center shadow-sm">
            <Heart className="w-4 h-4 text-white fill-white" />
          </div>
        </div>
      )}

      {/* Heart Animation */}
      <AnimatePresence>
        {showHeart && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 1.2, 1],
              opacity: [0, 1, 1],
              y: [0, -10, -20]
            }}
            exit={{
              scale: 0,
              opacity: 0,
              y: -30
            }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 25,
              duration: 1
            }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="relative">
              <Heart className="w-1/3 h-1/3 text-white fill-white drop-shadow-lg" />
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 0.3 }}
                transition={{ delay: 0.1 }}
                className="absolute inset-0 w-1/3 h-1/3 bg-white rounded-full blur-sm"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DoubleTapImage; 